import { useState } from 'react';
import { KeyRound, Lock, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ROLE_WORDS, errorWords, removeApprovalCode, setApprovalCode, validCode } from '@/lib/approvalCodes';

// Approvers' own approval codes (POS Login Access page, and the Approval codes page's
// first-switch prompt). An owner / manager enters THEIR code on the POS to approve a
// switched-on action; it is not their POS PIN. Each person sets / changes their own;
// the owner may set or remove anyone's (a locked code is unlocked by a new one).

function CodeForm({ token, person, onSaved, onCancel, mine }) {
  const [code, setCode] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const digits = (v) => v.replace(/\D/g, '').slice(0, 6);
  const mismatch = again.length > 0 && again !== code;
  const save = async () => {
    setBusy(true);
    try {
      const view = await setApprovalCode(token, person.person, code);
      toast.success(mine ? 'Your approval code is saved' : `${person.name}'s approval code is saved`);
      onSaved(view);
    } catch (e) {
      toast.error(errorWords(e, 'Could not save the approval code'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 items-end mt-3" data-testid={`approval-code-form-${person.person}`}>
      <div className="sm:col-span-2">
        <Label className="text-xs text-slate-600">New approval code (4-6 digits)</Label>
        <Input type="password" inputMode="numeric" autoComplete="new-password" value={code}
          onChange={(e) => setCode(digits(e.target.value))} data-testid="approval-code-input" />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs text-slate-600">Type it again</Label>
        <Input type="password" inputMode="numeric" autoComplete="new-password" value={again}
          onChange={(e) => setAgain(digits(e.target.value))} data-testid="approval-code-again" />
      </div>
      <div className="sm:col-span-3 flex gap-2">
        <Button onClick={save} disabled={busy || !validCode(code) || again !== code} className="min-h-[44px]" data-testid="approval-code-save">
          {busy ? 'Saving...' : 'Save code'}
        </Button>
        <Button variant="outline" onClick={onCancel} className="min-h-[44px]">Cancel</Button>
      </div>
      {mismatch && <p className="sm:col-span-7 text-xs text-red-700">The two codes are not the same.</p>}
      <p className="sm:col-span-7 text-xs text-slate-500">Use digits that are not your POS PIN, not all the same, and not in a row (like 1234).</p>
    </div>
  );
}

export default function ApprovalCodesCard({ token, data, onChanged, compact = false }) {
  const [editing, setEditing] = useState(null);
  const me = data?.me || {};
  const approvers = data?.approvers || [];

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.person === me.person ? 'your' : `${a.name}'s`} approval code? `
      + 'They cannot approve on the POS until a new one is set.')) return;
    try {
      onChanged(await removeApprovalCode(token, a.person));
      toast.success('Approval code removed');
    } catch (e) {
      toast.error(errorWords(e, 'Could not remove the approval code'));
    }
  };

  return (
    <Card className="border-slate-200" data-testid="approval-codes-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" />Approval codes</CardTitle>
        <CardDescription>
          When the owner turns on an action under Staff &amp; Labor → Approval codes, an owner or manager approves it on
          the POS with their own code (not their POS PIN). After 5 wrong tries a code locks for 15 minutes.
        </CardDescription>
      </CardHeader>
      <CardContent className={compact ? 'pt-0' : ''}>
        {approvers.length === 0 ? (
          <p className="text-sm text-slate-600">No owner or manager login is linked to this restaurant yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {approvers.map((a) => {
              const mine = a.person === me.person;
              const canEdit = mine || me.can_change_any_code;
              return (
                <li key={a.person} className="py-3" data-testid={`approver-row-${a.person}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{a.name}{mine ? ' (you)' : ''}</span>
                      <Badge className={a.role === 'owner' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>{ROLE_WORDS[a.role] || a.role}</Badge>
                      {a.locked ? <Badge className="bg-red-100 text-red-800"><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                        : a.has_code ? <Badge className="bg-green-100 text-green-800">Code set</Badge>
                          : <Badge variant="outline" className="text-amber-800 border-amber-300">No code yet</Badge>}
                    </div>
                    {canEdit && editing !== a.person && (
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setEditing(a.person)} className="min-h-[44px]" data-testid={`approval-code-edit-${a.person}`}>
                          {a.has_code ? (mine ? 'Change my code' : 'Set a new code') : (mine ? 'Set my approval code' : 'Set a code')}
                        </Button>
                        {a.has_code && (
                          <Button variant="ghost" onClick={() => remove(a)} className="min-h-[44px] text-red-700" aria-label={`Remove ${a.name}'s approval code`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {editing === a.person && (
                    <CodeForm token={token} person={a} mine={mine} onCancel={() => setEditing(null)}
                      onSaved={(view) => { setEditing(null); onChanged(view); }} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
